import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, User as UserIcon, Coins } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { MessagesNavLink } from "@/components/chat/MessagesNavLink";

const linkCls = ({ isActive }) =>
  `text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${isActive ? "text-[#D4AF37]" : "text-neutral-400 hover:text-white"}`;

export const Navbar = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B0C0E]/80 border-b border-white/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 h-20 flex items-center justify-between gap-3 sm:gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <img src="/logo.svg" alt="YourHostess" className="w-10 h-10 sm:w-11 sm:h-11 transition-transform duration-500 group-hover:rotate-[-4deg]" data-testid="nav-logo-mark" />
          <span className="font-serif text-2xl tracking-wide whitespace-nowrap hidden sm:inline"><em className="text-[#D4AF37]">Your</em>Hostess</span>
        </Link>
        <nav className="hidden md:flex items-center gap-10">
          <NavLink to="/cerca" className={linkCls} data-testid="nav-search-link">Trova Hostess</NavLink>
          <NavLink to="/eventi" className={linkCls} end data-testid="nav-events-link">Eventi</NavLink>
          {user?.role === "client" && <NavLink to="/eventi/nuovo" className={linkCls} data-testid="nav-create-event-link">Crea Evento</NavLink>}
          {user && user.role !== "admin" && <MessagesNavLink className={linkCls} testid="nav-messages-link" />}
          {user && user.role !== "admin" && <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard-link">Dashboard</NavLink>}
          {user?.role === "admin" && <NavLink to="/admin" className={linkCls} data-testid="nav-admin-link">Admin</NavLink>}
        </nav>
        <div className="flex items-center gap-3 sm:gap-4">
          {user ? (
            <>
              {(user.role === "client" || user.role === "hostess") && (
                <Link to="/crediti" className="flex items-center gap-1.5 border border-[#D4AF37]/40 px-3 py-1.5 text-xs font-mono text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors" data-testid="nav-credits-badge">
                  <Coins className="w-3.5 h-3.5" /> {user.credits ?? 0}
                </Link>
              )}
              <NotificationBell />
              <Link to={user.role === "hostess" ? "/profilo" : user.role === "admin" ? "/admin" : "/dashboard"} className="hidden sm:flex items-center gap-2 text-sm text-neutral-300 hover:text-[#D4AF37] transition-colors" data-testid="nav-user-link">
                <UserIcon className="w-4 h-4" /> {user.name.split(" ")[0]}
                <span className="eyebrow !text-[0.6rem] !text-neutral-500">{user.role === "hostess" ? "Hostess" : user.role === "admin" ? "Admin" : "Cliente"}</span>
              </Link>
              <button onClick={async () => { await logout(); nav("/"); }} className="text-neutral-500 hover:text-white transition-colors" data-testid="nav-logout-button" aria-label="Esci">
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/accedi" className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-neutral-300 hover:text-white" data-testid="nav-login-button">Accedi</Link>
              <Link to="/accedi?mode=register" className="btn-gold !px-3 sm:!px-5 !py-2 sm:!py-2.5 !text-[11px] sm:!text-xs !tracking-[0.12em] sm:!tracking-[0.18em]" data-testid="nav-register-button">Registrati</Link>
            </>
          )}
        </div>
      </div>
      <nav className="md:hidden flex items-center gap-6 px-6 pb-3 overflow-x-auto">
        <NavLink to="/cerca" className={linkCls} data-testid="nav-mobile-search-link">Trova</NavLink>
        <NavLink to="/eventi" className={linkCls} end data-testid="nav-mobile-events-link">Eventi</NavLink>
        {user && user.role !== "admin" && <MessagesNavLink className={linkCls} testid="nav-mobile-messages-link" label="Chat" />}
        {user && <NavLink to={user.role === "admin" ? "/admin" : "/dashboard"} className={linkCls} data-testid="nav-mobile-dashboard-link">{user.role === "admin" ? "Admin" : "Dashboard"}</NavLink>}
      </nav>
    </header>
  );
};
